import { probe } from '@geo-admin/shared/contract/api/index.ts';
import { SHORTFALL_WARN_RATIO } from '@geo-admin/shared/contract/probe-values.ts';
import { Link } from '@tanstack/react-router';
import { RateCell } from '../components/RateCell.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi } from '../lib/api.ts';
import { dateTime, OUTCOME_LABEL, relative } from '../lib/format.ts';
import { PageTitle } from '../components/PageTitle.tsx';

export function HealthPage() {
  const h = useApi(probe.health, {}, { refetchInterval: 30_000 });
  if (h.isLoading) return <Loading />;
  if (h.error) return <ErrorBox error={h.error} />;
  const d = h.data!;
  const util = d.capacity.utilization;
  return (
    <>
      <PageTitle title="수집 상태" description={`KST ${d.today} · ${dateTime(d.now)} 기준`} />
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader title="스케줄러" />
          <CardBody className="space-y-2 text-sm">
            {d.scheduler.length === 0 ? (
              <Badge tone="red">한 번도 돌지 않았습니다 — Cloud Scheduler·OIDC audience 확인</Badge>
            ) : null}
            {d.scheduler.map((j) => (
              <div key={j.name}>
                <div className="flex items-center gap-2">
                  <b>{j.name}</b>
                  {j.stale ? <Badge tone="red">15분 넘게 성공 없음</Badge> : <Badge tone="green">정상</Badge>}
                </div>
                <div className="text-xs text-slate-500">
                  마지막 성공 {relative(j.lastOkAt)} · 시작 {relative(j.lastStartedAt)}
                </div>
                {j.lastError ? <div className="text-xs text-bad">{j.lastError}</div> : null}
              </div>
            ))}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="오늘 수집" />
          <CardBody className="grid grid-cols-3 gap-2 text-sm">
            {(
              [
                ['쓸 수 있는 샘플', `${d.collection.usable} / ${d.collection.target}`],
                ['진행 중', d.collection.inFlight],
                ['어제 결손', d.missedYesterday],
              ] as const
            ).map(([k, v]) => (
              <div key={k}>
                <div className="text-xs text-slate-500">{k}</div>
                <div className="text-lg font-semibold tabular-nums">{v}</div>
              </div>
            ))}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="용량" description="판단 근거는 이론 공급이 아니라 최근 실제 달성입니다" />
          <CardBody className="space-y-1 text-sm">
            <p>
              수요 {d.capacity.demandPerDay}/일 · <b>실제 달성 {d.capacity.achievedPerDay}/일</b>{' '}
              <span className="text-xs text-slate-500">
                {d.capacity.windowDays > 0 ? `최근 ${d.capacity.windowDays}일 평균` : '표본 없음'}
              </span>
            </p>
            <p>
              결손률 {d.capacity.shortfallRatio === null ? '—' : `${Math.round(d.capacity.shortfallRatio * 100)}%`}{' '}
              {d.capacity.shortfallRatio !== null && d.capacity.shortfallRatio > SHORTFALL_WARN_RATIO ? (
                <Badge tone="amber">수요를 못 채우고 있습니다</Badge>
              ) : null}
            </p>
            {/* 이론값은 참고다 — 간격에서 나온 산술이라 밤 시간대 저하를 반영하지 않는다 */}
            <p className="text-xs text-slate-500">
              이론 공급 {d.capacity.supplyPerDay}/일 · 이용률{' '}
              {util === null ? '— (켜진 워커 없음)' : `${Math.round(util * 100)}%`} — 이론은 밤 시간대 저하를 반영하지
              않습니다
            </p>
          </CardBody>
        </Card>
      </div>
      <Card>
        <CardHeader
          title="워커별"
          description="no-answer 감시 = (답변 컨테이너 없음 + 제출 후 멈춤) ÷ 제출 성공 — 차단은 blocked 로 오지 않는다"
        />
        <CardBody className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-slate-500">
                <th className="px-3 py-2">워커</th>
                <th className="px-3 py-2">no-answer 6시간</th>
                <th className="px-3 py-2">오류 종류 24시간</th>
                <th className="px-3 py-2">최소 시작 간격 24시간</th>
              </tr>
            </thead>
            <tbody>
              {d.workers.map((w) => (
                <tr key={w.id} className="border-b border-slate-100 align-top">
                  <td className="px-3 py-2">
                    <b>{w.name}</b> {w.alive ? null : <Badge tone="red">응답 없음</Badge>}{' '}
                    {w.pausedUntil ? <Badge tone="amber">정지</Badge> : null}
                    {w.pauseReason ? <div className="text-xs text-amber-800">{w.pauseReason}</div> : null}
                  </td>
                  <td className="px-3 py-2">
                    <RateCell rate={w.noAnswer6h} />
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {Object.entries(w.errorKinds24h)
                      .map(([k, n]) => `${k} ${n}`)
                      .join(' · ') || '—'}
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    {w.minStartGapSec24h === null ? '—' : `${Math.round(w.minStartGapSec24h)}초`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="최근 샘플" />
        <CardBody className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <tbody>
              {d.recentSamples.map((s) => (
                <tr key={s.id} className="border-b border-slate-100">
                  <td className="px-3 py-1.5 text-slate-500">{dateTime(s.createdAt)}</td>
                  <td className="px-3 py-1.5">{s.workerName}</td>
                  <td className="px-3 py-1.5">
                    <Link to="/admin/samples/$id" params={{ id: s.id }} className="hover:underline">
                      {s.queryText}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5">
                    {s.outcome ? OUTCOME_LABEL[s.outcome] : '진행 중'}
                    {s.errorKind ? ` · ${s.errorKind}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </>
  );
}
